import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
  KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
} from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import {
  Order,
  getValueFromName,
  sleep,
  stableSort,
  titleStr,
} from "../lib/helper";
import "../styles/table.list.css";
import {
  Badge,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  TextField,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import {
  HeadCell,
  Pagination,
  PaginationFilterType,
  PaginationFilterValue,
  TableApiRes,
} from "../models/base.model";
import TableActions, { ActionItem } from "./TableActions";
import TableAddButton from "./TableAddButton";
import TableFilter, { FilterGroup } from "./TableFilter";
import NoResult from "./NoResult";
import { uniqBy } from "lodash";
import { AdminPermission } from "../models/auth.model";

const NoBorderTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: 0,
}));

interface EnhancedTableProps {
  onRequestSort: (property: string, event?: React.MouseEvent<unknown>) => void;
  order: Order;
  orderBy: string;
  headCells: readonly HeadCell[];
  noAction?: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, noAction } = props;
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(property, event);
    };

  return (
    <TableHead>
      <TableRow>
        {props.headCells.map((headCell) => (
          <NoBorderTableCell
            className="head-th"
            key={headCell.id}
            width={headCell.width}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label || titleStr(headCell.id)}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </NoBorderTableCell>
        ))}
        {!noAction && (
          <NoBorderTableCell width={32} className="head-th">
            Actions
          </NoBorderTableCell>
        )}
      </TableRow>
    </TableHead>
  );
}

interface BaseEventProps<T> {
  onItemClick?: (item: T, event?: React.MouseEvent<unknown>) => void;
  onLoad: (param: Pagination) => Promise<TableApiRes<T>>;
  rowsPerPage?: number;
  onAdd?: () => Promise<T | T[]>;
  onAddBtnText?: string;
  onEdit?: (record: T) => Promise<T>;
  onDelete?: (record: T) => Promise<void | string[]>;
  noAction?: boolean;
  searchKeys?: (keyof T)[];
  searchText?: string;
  searchFilterType?: PaginationFilterType;
  initialSortKey?: string;
  initialOrder?: Order;
  uid: keyof T;
  addButtonClassName?: string;
  permissions?: AdminPermission[];
  hideRefresh?: boolean;
  actions?: (row: T) => ActionItem[];
  filters?: FilterGroup<T>[];
}

interface TableProps<T> extends BaseEventProps<T> {
  headCells: readonly HeadCell[];
}

interface GridProps<T> extends BaseEventProps<T> {
  onItemClick?: (item: T, event?: React.MouseEvent<unknown>) => void;
  renderItem: JSX.Element;
  sorts: (keyof T)[];
}

interface TableFilterRef {
  open: () => void;
}

let searchValue = "";
let filterValue: any;

const EnhancedTable = React.forwardRef(
  <T extends unknown>(props: TableProps<T> | GridProps<T>, ref?: any) => {
    const [order, setOrder] = React.useState<Order>(
      props.initialOrder || "desc"
    );
    const { searchKeys, onLoad, rowsPerPage = 20, searchFilterType = PaginationFilterType.contains } = props;
    const [rows, setRows] = React.useState<T[]>([]);
    const [total, setTotal] = React.useState(0);
    const [offset, setOffset] = React.useState<any>(undefined);
    const [orderBy, setOrderBy] = React.useState(
      props.initialSortKey ||
      ("headCells" in props
        ? props.headCells[0].id
        : props.sorts[0]?.toString())
    );
    const [page, setPage] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const filterRef = React.useRef<TableFilterRef>(null);

    const getFilter = () => {
      if (searchValue && !filterValue) {
        return {
          or: (searchKeys || []).map((k) => ({
            key: k as string,
            values: [searchValue],
            type: searchFilterType,
          })),
        };
      }
      let paginationFilters: PaginationFilterValue[] = [];
      if (filterValue) {
        Object.keys(filterValue).forEach((key) => {
          const values: any = filterValue[key];
          if (!values) {
            return;
          }
          if (["createdAt", "updatedAt"].includes(key)) {
            paginationFilters.push({
              key,
              type:
                values.from && values.to
                  ? PaginationFilterType.between
                  : values.from
                    ? PaginationFilterType.greater
                    : PaginationFilterType.less,
              values: [values.from, values.to].filter((a) => a),
            });
          } else {
            paginationFilters.push({
              key,
              type: PaginationFilterType.in,
              values: Array.isArray(values) ? values : [values],
            });
          }
        });
      }
      return {
        and: paginationFilters.concat(
          searchValue
            ? (searchKeys || []).map((k) => ({
              key: k as string,
              values: [searchValue],
              type: searchFilterType,
            }))
            : []
        ),
      };
    };

    async function onSearchFilter() {
      try {
        setLoading(true);
        const { data, total, offset } = await onLoad({
          limit: rowsPerPage,
          needTotal: true,
          filter: getFilter(),
        });
        setTotal(total || 0);
        setOffset(offset);
        setRows(data);
      } catch (error) {
        console.error(error);
      } finally {
        setPage(0);
        setLoading(false);
      }
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const { data, total, offset } = await props.onLoad({
          limit: rowsPerPage,
          needTotal: true,
        });
        setTotal(total || 0);
        setOffset(offset);
        setRows(data);
      } catch (error) {
        setRows([]);
        console.error(error);
      } finally {
        setPage(0);
        setLoading(false);
      }
    };

    React.useImperativeHandle(ref, () => ({
      onDelete(record: T) {
        updateData(record, true);
      },
      onRefresh(records?: T[]) {
        if (records) {
          setRows([...records]);
        } else {
          fetchData();
        }
      },
    }));

    React.useEffect(() => {
      fetchData();
      return () => {
        searchValue = "";
        filterValue = undefined;
      };
    }, []);

    // TODO need to figure out how to make sort work
    const handleRequestSort = (property: string) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
      if (newPage + 1 > Math.ceil(rows.length / rowsPerPage)) {
        try {
          setLoading(true);
          const { data, ...result } = await props.onLoad({
            limit: rowsPerPage,
            offset,
            filter: getFilter(),
          });
          setOffset(result?.offset);
          setRows(rows.concat(data));
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
      setPage(newPage);
    };

    const onAdd = async () => {
      if (!props.onAdd) {
        return;
      }
      const record = await props.onAdd();
      if (!record) {
        return;
      }
      const res = Array.isArray(record) ? record : [record];
      setPage(0);
      setRows(uniqBy(res.concat(rows || []), props.uid));
    };

    const onEdit = async (record: T) => {
      if (!props.onEdit) {
        return;
      }
      const row = await props.onEdit(record);
      updateData(row);
    };

    const updateData = (row: T, remove?: boolean) => {
      const i = rows.findIndex((a) => a[props.uid] === row[props.uid]);
      if (i >= 0) {
        remove ? rows.splice(i, 1) : rows.splice(i, 1, row);
        setRows([...(rows || [])]);
      }
    };

    const onDelete = async (record: T) => {
      if (!props.onDelete) {
        return;
      }
      const res = await props.onDelete(record);
      if (res && Array.isArray(res)) {
        for (const val of res) {
          const v: any = {};
          v[props.uid] = val;
          updateData(v, true);
        }
      } else {
        updateData(record, true);
      }
    };

    return (
      <div>
        {props.onAdd && (
          <TableAddButton
            className={props.addButtonClassName}
            onClick={onAdd}
            title={props.onAddBtnText || "New"}
            data-testid={props.onAddBtnText || "New"}
          />
        )}
        <Paper elevation={"renderItem" in props ? 0 : 1}>
          <Box
            className="between-wrapper"
            sx={{ mt: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}
          >
            {props.searchText && <Tooltip placement="top" title={props.searchText}>
              <FormControl
                sx={{ width: { xs: "100%", sm: "auto" }, mb: 2, ml: 1 }}
                size="small"
                variant="outlined"
              >
                <InputLabel>Search</InputLabel>
                <OutlinedInput
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && !loading) {
                      onSearchFilter();
                    }
                  }}
                  onChange={(e) => (searchValue = e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        disabled={loading}
                        aria-label="toggle password visibility"
                        onClick={onSearchFilter}
                        edge="end"
                      >
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
              </FormControl>
            </Tooltip>}
            <Box
              className="between-wrapper"
              sx={{ mb: 2, width: { xs: "100%", sm: "auto" } }}
            >
              <div className="center-wrapper">
                {!props.hideRefresh && (
                  <Tooltip placement="top" title="Refresh">
                    <span className="margin-r-normal">
                      <IconButton disabled={loading} onClick={fetchData}>
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {props.filters && (
                  <Tooltip placement="top" title="Filter list">
                    <span className="margin-r-normal">
                      <IconButton
                        onClick={filterRef.current?.open}
                        disabled={loading}
                      >
                        <Badge
                          color="secondary"
                          variant="dot"
                          invisible={!filterValue}
                        >
                          <FilterListIcon />
                        </Badge>
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </div>
              {"sorts" in props && (
                <Tooltip placement="top" title="Sort by">
                  <TextField
                    select
                    size="small"
                    className="margin-r-normal"
                    label={`Sort ${order}`}
                    value={orderBy}
                    onChange={(e) => setOrderBy(e.target.value)}
                    SelectProps={{
                      IconComponent: () => (
                        <span style={{ display: "inline-block" }}></span>
                      ),
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setOrder(order === "asc" ? "desc" : "asc")
                            }
                            edge="end"
                          >
                            {order === "desc" ? (
                              <KeyboardDoubleArrowDownIcon />
                            ) : (
                              <KeyboardDoubleArrowUpIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  >
                    {props.sorts.map((d) => {
                      const s = d.toString();
                      return (
                        <MenuItem key={s} value={s}>
                          {titleStr(s)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Tooltip>
              )}
            </Box>
          </Box>
          {props.filters && (
            <TableFilter<T>
              ref={filterRef}
              allData={[]}
              filters={props.filters}
              onFilter={(v) => {
                filterValue = v;
                onSearchFilter();
              }}
            />
          )}
          <TableContainer
            className="relative"
            style={{ minHeight: 200, padding: "0 2px" }}
          >
            {"headCells" in props && (
              <Table
                sx={{ minWidth: 750 }}
                stickyHeader
                aria-labelledby="tableTitle"
                size={"medium"}
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headCells={props.headCells}
                  noAction={props.noAction}
                />
                <TableBody>
                  {rows &&
                    (rows.length > 0 ? (
                      rows
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => {
                          return (
                            <TableRow
                              data-testid={row[props.uid]}
                              hover
                              className="pointer"
                              onClick={(e) =>
                                props.onItemClick
                                  ? props.onItemClick(row, e)
                                  : undefined
                              }
                              tabIndex={-1}
                              key={"row_" + index}
                            >
                              {props.headCells.map((h) => {
                                const val = getValueFromName(row, h.id);
                                return (
                                  <NoBorderTableCell
                                    key={`${h.id}_${index}`}
                                    {...(!h.render && {
                                      "data-testid": `${h.id}`,
                                    })}
                                  >
                                    {
                                      (h.render
                                        ? h.render(val, row, rows)
                                        : val) as any
                                    }
                                  </NoBorderTableCell>
                                );
                              })}
                              {!props.noAction && (
                                <NoBorderTableCell>
                                  <TableActions
                                    onEdit={
                                      props.onEdit
                                        ? () => onEdit(row)
                                        : undefined
                                    }
                                    onDelete={
                                      props.onDelete
                                        ? () => onDelete(row)
                                        : undefined
                                    }
                                    additionalActions={
                                      props.actions
                                        ? props.actions(row)
                                        : undefined
                                    }
                                  />
                                </NoBorderTableCell>
                              )}
                            </TableRow>
                          );
                        })
                    ) : (
                      <TableRow
                        style={{
                          height: 200,
                        }}
                      >
                        <NoBorderTableCell colSpan={props.headCells.length}>
                          <NoResult />
                        </NoBorderTableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            {"renderItem" in props && (
              <Grid container spacing={3}>
                {rows &&
                  (rows.length > 0 ? (
                    stableSort<T>(rows, order, orderBy)
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        return (
                          <Grid
                            key={"row_" + row[props.uid]}
                            item
                            md={4}
                            lg={3}
                            xl={2}
                            sm={6}
                            xs={12}
                          >
                            <Paper
                              sx={{ borderRadius: "16px", overflow: "hidden" }}
                            >
                              {React.cloneElement(props.renderItem, {
                                onEditClick: () => {
                                  if (props.onEdit) {
                                    onEdit(row);
                                  }
                                },
                                onOtherClick: async () => {
                                  if (props.renderItem.props.onOtherClick) {
                                    const res =
                                      await props.renderItem.props.onOtherClick(
                                        row
                                      );
                                    if (res) {
                                      updateData(res);
                                    }
                                  }
                                },
                                onDeleteClick: async () => {
                                  await onDelete(row);
                                },
                                row: row,
                                index: index,
                              })}
                            </Paper>
                          </Grid>
                        );
                      })
                  ) : (
                    <NoResult />
                  ))}
              </Grid>
            )}
            {rows && (
              <TablePagination
                rowsPerPageOptions={[rowsPerPage]}
                component="div"
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    display: { sm: "flex", xs: "grid" },
                    gridTemplateColumns: "repeat(2,50%)",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    gridColumnStart: 1,
                  },
                }}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
              />
            )}
            {loading && (
              <div
                className="width-100p height-100p all-center-wrapper common-loading"
                data-testid={"tableListLoading"}
              >
                <CircularProgress />
              </div>
            )}
          </TableContainer>
        </Paper>
      </div>
    );
  }
);

export default EnhancedTable as unknown as <T extends unknown>(
  props: (TableProps<T> | GridProps<T>) & { ref?: any }
) => JSX.Element;
